import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslocoModule } from '@jsverse/transloco';
import { DateTime } from 'luxon';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';
import { provideAppDateAdapter } from '@ukam/core/date-adapter/app-date-adapter.provider';
import { FilterField } from '@ukam/core/filters/models/filter-field';

interface DateFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

const DATE_FILTER_OPERATORS: DateFilterOperatorOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'eq', label: 'Equal to' },
  { value: 'ne', label: 'Not equal to' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal to' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal to' },
  { value: 'btw', label: 'Between' },
];

@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    provideAppDateAdapter(),
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppDateFilterComponent), multi: true },
  ],
})
export class AppDateFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = DATE_FILTER_OPERATORS;

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;
  value: DateTime | null = null;
  start: DateTime | null = null;
  end: DateTime | null = null;
  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(400)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get isToday(): boolean {
    return this.operator === 'today';
  }

  get isRange(): boolean {
    return this.operator === 'btw';
  }

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    const patch: Partial<FilterField> = { operator: op };

    if (op === 'btw') {
      patch.value = undefined;
    } else if (op === 'today') {
      patch.value = undefined;
      patch.start = undefined;
      patch.end = undefined;
    } else {
      patch.start = undefined;
      patch.end = undefined;
    }

    this.operator = op;
    this.value = this.parse(patch.value);
    this.start = this.parse(patch.start);
    this.end = this.parse(patch.end);
    this.emit(patch);
  }

  onValueChange(date: DateTime | null): void {
    this.value = date;
    this.emit({ value: this.format(date) });
  }

  onStartChange(date: DateTime | null): void {
    this.start = date;
    this.emit({ start: this.format(date) });
  }

  onEndChange(date: DateTime | null): void {
    this.end = date;
    this.emit({ end: this.format(date) });
  }

  clear(): void {
    this.value = null;
    this.start = null;
    this.end = null;
    this.emit({ value: undefined, start: undefined, end: undefined });
  }

  private parse(value: string | undefined | null): DateTime | null {
    if (!value) return null;
    const date = DateTime.fromISO(value);
    return date.isValid ? date : null;
  }

  private format(date: DateTime | null): string | undefined {
    if (!date) return undefined;
    return date.toFormat('yyyy-MM-dd');
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;
    this.value = this.parse(this.field?.value);
    this.start = this.parse(this.field?.start);
    this.end = this.parse(this.field?.end);
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
